import { LitElement, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";

import { DataQueryController } from "ui-common";
import "ui-components/skeleton-circle";
import "ui-components/skeleton-rect";
import "ui-components/social-media-item/social-media-item";
import { parseSocialMediaUrls, platformConfigs, type ParsedSocialLink } from "ui-common/socialMediaConfig";
import { emailValidation } from "ui-common/socialMediaBaseConfig";

import { styles } from "./social-links-section.css";

import { profileDataQueryOptions } from "../../queries/profile-data";
import { t, translate } from "../../translations/i18n";
import { createRef, ref } from "lit/directives/ref.js";
import { DnDContainerController, type DraggedItem } from "../../controllers/dnd-container-controller";
import { DnDDropIndicatorController } from "../../controllers/dnd-drop-indicator-controller";

@customElement("social-links-section")
export class SocialLinksSection extends LitElement {
  static override styles = styles;

  @property({ type: Array })
  socialLinks?: string[];

  @property({ type: Boolean })
  editable: boolean = false;

  @property({ type: Number })
  autoFocusIndex: number = -1;

  @state()
  private validationErrors = new Map<number, string>();

  @state()
  private hasSubmitAttempted = false;

  @state()
  private touchedInputs = new Set<number>();

  @state()
  private flourishLink: string | null = null;

  private profileDataQuery = new DataQueryController(this, () => profileDataQueryOptions);

  // Create refs for the new controller architecture
  private containerRef = createRef<HTMLElement>();

  private dndController = new DnDContainerController<DraggedItem>(this, this.containerRef, {
    onReorder: async (newOrder: DraggedItem[]) => {
      const links = newOrder.map((link) => link.id);

      // Emit event to update form state
      this.dispatchEvent(
        new CustomEvent("social-links-changed", {
          detail: { socialLinks: links },
          bubbles: true,
          composed: true,
        }),
      );
    },
    onDropComplete: (droppedItem: DraggedItem) => {
      this.flourishLink = droppedItem.id;

      setTimeout(() => {
        this.flourishLink = null;
      }, 600);
    },
    getValidDropPositions: () => ["top", "bottom"],
  });

  private dropIndicatorController = new DnDDropIndicatorController(this, this.containerRef);

  private handleDnDDropRequested = () => {
    if (this.socialLinks) {
      const socialLinks = [...this.socialLinks];
      const links: DraggedItem[] = socialLinks.map((l) => ({ id: l }));
      this.dndController.processDropWithItems(links);
    }
  };

  private get parsedSocialLinks(): ParsedSocialLink[] {
    // Use passed socialLinks if available, otherwise fall back to query data
    const links = this.socialLinks ?? this.profileDataQuery.result.data?.socialLinks;
    if (!links) {
      return [];
    }
    return parseSocialMediaUrls(links);
  }

  private normalizeUrlForValidation(input: string): string {
    const trimmed = input.trim();

    if (!trimmed || emailValidation(trimmed)) {
      return trimmed;
    }

    if (!trimmed.startsWith("https://") && !trimmed.startsWith("http://")) {
      return trimmed;
    }

    try {
      const url = new URL(trimmed);
      return url.href;
    } catch {
      const manuallyEncoded = trimmed
        .split("")
        .map((char) => {
          const code = char.charCodeAt(0);
          return code > 127 ? encodeURIComponent(char) : char;
        })
        .join("");
      try {
        const url = new URL(manuallyEncoded);
        return url.href;
      } catch {
        return trimmed;
      }
    }
  }

  private validateSocialLink(url: string, linkIndex?: number): { isValid: boolean; errorMessage?: string } {
    const normalizedUrl = this.normalizeUrlForValidation(url);

    // 1. Empty validation - but be more lenient for untouched email fields
    if (!normalizedUrl.trim()) {
      // If this is an untouched field and we suspect it might be for email, don't show error yet
      if (linkIndex !== undefined && !this.touchedInputs.has(linkIndex) && !this.hasSubmitAttempted) {
        // Check if this might be an email field by looking at the corresponding link
        const parsedLink = this.parsedSocialLinks[linkIndex];
        if (parsedLink && parsedLink.platform === "email") {
          return { isValid: false, errorMessage: "" }; // Invalid but no error message yet
        }
      }
      return { isValid: false, errorMessage: translate("profile.validation.emptyLink") };
    }

    if (emailValidation(normalizedUrl.trim())) {
      return { isValid: true };
    }

    // 2. URL format validation
    try {
      const parsedUrl = new URL(normalizedUrl);

      // Must be HTTPS
      if (parsedUrl.protocol !== "https:") {
        return { isValid: false, errorMessage: translate("profile.validation.httpsRequired") };
      }

      // Must match pattern: https://domain.com or https://www.domain.com
      const hostname = parsedUrl.hostname;
      const cleanHostname = hostname.replace(/^www\./, "");

      // 3. Platform validation using existing config
      const platformConfig = platformConfigs.find(
        (config) => config.domains.includes(cleanHostname) || config.domains.includes(hostname),
      );

      if (!platformConfig) {
        return { isValid: false, errorMessage: translate("profile.validation.unsupportedPlatform") };
      }

      // Validate URL against the platform-specific regex
      if (!platformConfig.validation(normalizedUrl)) {
        return { isValid: false, errorMessage: translate("profile.validation.invalidUrlFormat") };
      }

      return { isValid: true };
    } catch {
      return { isValid: false, errorMessage: translate("profile.validation.invalidUrlFormat") };
    }
  }

  private validateAndUpdateError(url: string, linkIndex: number) {
    const validation = this.validateSocialLink(url, linkIndex);
    if (!validation.isValid && validation.errorMessage) {
      this.validationErrors.set(linkIndex, validation.errorMessage);
    } else {
      this.validationErrors.delete(linkIndex);
    }
    this.validationErrors = new Map(this.validationErrors);
  }

  public validateAllLinks(): boolean {
    this.hasSubmitAttempted = true;

    let hasErrors = false;
    this.socialLinks?.forEach((link, index) => {
      const validation = this.validateSocialLink(link, index);
      if (!validation.isValid) {
        hasErrors = true;
        // Always show error message when submit is attempted
        this.validationErrors.set(index, validation.errorMessage || translate("profile.validation.emptyLink"));
      } else {
        this.validationErrors.delete(index);
      }
    });

    this.validationErrors = new Map(this.validationErrors);

    return !hasErrors;
  }

  private handleAddMoreClick() {
    // Emit custom event for parent components to handle
    this.dispatchEvent(
      new CustomEvent("add-more-clicked", {
        bubbles: true,
        composed: true,
      }),
    );
  }

  private handleSocialLinkClick(social: ParsedSocialLink) {
    // Emit custom event with social link data
    this.dispatchEvent(
      new CustomEvent("social-link-clicked", {
        detail: { social },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private handleLinkInput(event: CustomEvent, linkIndex: number) {
    if (!this.editable) return;

    const newUrl = event.detail.value;
    const currentLinks = this.socialLinks ? [...this.socialLinks] : [];
    currentLinks[linkIndex] = newUrl;

    // Mark this input as touched
    this.touchedInputs.add(linkIndex);
    this.touchedInputs = new Set(this.touchedInputs);

    // Real-time validation
    this.validateAndUpdateError(newUrl, linkIndex);

    // Emit social-links-changed event to parent
    this.dispatchEvent(
      new CustomEvent("social-links-changed", {
        detail: { socialLinks: currentLinks },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private handleLinkBlur(event: CustomEvent, linkIndex: number) {
    if (!this.editable) return;

    const rawUrl = event.detail.value;
    const normalizedUrl = this.normalizeUrlForValidation(rawUrl);
    const currentLinks = this.socialLinks ? [...this.socialLinks] : [];
    currentLinks[linkIndex] = normalizedUrl;

    // Mark this input as touched
    this.touchedInputs.add(linkIndex);
    this.touchedInputs = new Set(this.touchedInputs);

    this.validateAndUpdateError(normalizedUrl, linkIndex);

    // Emit social-links-changed event to parent
    this.dispatchEvent(
      new CustomEvent("social-links-changed", {
        detail: { socialLinks: currentLinks },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private handleDeleteLink(event: CustomEvent, linkIndex: number) {
    if (!this.editable) return;

    const currentLinks = this.socialLinks ? [...this.socialLinks] : [];
    currentLinks.splice(linkIndex, 1); // Remove the link at the specified index

    // Clean up touched inputs and validation errors - shift indices down for items after deleted index
    const newTouchedInputs = new Set<number>();
    const newValidationErrors = new Map<number, string>();

    this.touchedInputs.forEach((touchedIndex) => {
      if (touchedIndex < linkIndex) {
        newTouchedInputs.add(touchedIndex);
      } else if (touchedIndex > linkIndex) {
        newTouchedInputs.add(touchedIndex - 1);
      }
      // Skip touchedIndex === linkIndex (the deleted item)
    });

    this.validationErrors.forEach((error, errorIndex) => {
      if (errorIndex < linkIndex) {
        newValidationErrors.set(errorIndex, error);
      } else if (errorIndex > linkIndex) {
        newValidationErrors.set(errorIndex - 1, error);
      }
      // Skip errorIndex === linkIndex (the deleted item)
    });

    this.touchedInputs = newTouchedInputs;
    this.validationErrors = newValidationErrors;

    // Emit social-links-changed event
    this.dispatchEvent(
      new CustomEvent("social-links-changed", {
        detail: { socialLinks: currentLinks },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private renderSocialLinks() {
    const { isPending } = this.profileDataQuery.result;

    if (isPending) {
      return Array.from(
        { length: 3 },
        () => html`
          <skeleton-rect width="100%" height="60px" elevation="1">
            <skeleton-circle size="32px" elevation="0"></skeleton-circle>
            <div class="skeleton-text-group">
              <skeleton-rect width="80px" height="12px" elevation="0"></skeleton-rect>
              <skeleton-rect width="120px" height="16px" elevation="0"></skeleton-rect>
            </div>
          </skeleton-rect>
        `,
      );
    }

    if (this.profileDataQuery.result.error) {
      return html`<p>${t("profile.socialLinksError")} ${this.profileDataQuery.result.error.message}</p>`;
    }

    if (this.parsedSocialLinks.length === 0) {
      return html`<p>${t("profile.noSocialLinksFound")}</p>`;
    }

    return this.parsedSocialLinks.map((social, index) => {
      const shouldAutoFocus = this.autoFocusIndex === index;

      // If this item should auto-focus, emit the auto-focus-used event
      if (shouldAutoFocus) {
        // Use setTimeout to emit after render
        setTimeout(() => {
          this.dispatchEvent(
            new CustomEvent("auto-focus-used", {
              bubbles: true,
              composed: true,
            }),
          );
        }, 0);
      }

      // Get validation state for this link
      const hasError = this.validationErrors.has(index);
      const errorMessage = this.validationErrors.get(index) || "";

      // Show validation if submit attempted OR if this input has been touched and has an error
      const showValidation = this.hasSubmitAttempted || (this.touchedInputs.has(index) && hasError);

      return html`
        <social-media-item
          .index=${index}
          .icon=${social.icon}
          .platformName=${social.platformName}
          .link=${social.originalUrl}
          .mode=${this.editable ? "edit" : "display"}
          .showDelete=${this.editable}
          .deleteAriaLabel=${this.editable
            ? translate("profile.deleteLinkAriaLabel", { platformName: social.platformName })
            : ""}
          .autoFocus=${shouldAutoFocus}
          .hasError=${hasError}
          .errorMessage=${errorMessage}
          .showValidation=${showValidation}
          .flourish=${this.flourishLink === social.originalUrl}
          @click=${() => this.handleSocialLinkClick(social)}
          @link-input=${(e: CustomEvent) => this.handleLinkInput(e, index)}
          @link-blur=${(e: CustomEvent) => this.handleLinkBlur(e, index)}
          @delete-link=${(e: CustomEvent) => this.handleDeleteLink(e, index)}
        ></social-media-item>
        ${this.dropIndicatorController.renderDropIndicator(
          this.dndController.currentDropPosition,
          this.dndController.currentDropPosition ? this.dndController.getValidDropPositions() : [],
          this.dndController.isDragging,
        )}
      `;
    });
  }

  protected override render() {
    const listClasses = {
      "social-links-list": true,
      "drag-active": this.dndController.isDragging,
    };
    return html`
      <div class="social-links-section">
        <h3 class="social-links-title">${t("profile.addYourSocials")}</h3>
        <div
          ${ref(this.containerRef)}
          class="${classMap(listClasses)}"
          style="position: relative;"
          @dnd-drop-requested=${this.handleDnDDropRequested}
        >
          ${this.renderSocialLinks()}
          ${!this.profileDataQuery?.result.isPending
            ? html`<button class="add-more-button" @click=${this.handleAddMoreClick}>
                <div class="add-more-icon-container">
                  <ph-plus-circle class="add-more-icon" size="16"></ph-plus-circle>
                </div>
                <p class="add-more-text">${t("profile.addMore")}</p>
              </button>`
            : undefined}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "social-links-section": SocialLinksSection;
  }
}
